/**
 * PhasePreviewModal — Cinematic preview of a locked or upcoming phase.
 * Shows: mechanisms, training principle, macros, KPIs, gender adjustments.
 * Creates anticipation for locked phases + reference for current one.
 *
 * Props:
 *   open: boolean
 *   phaseId: 1..4
 *   onClose: callback
 */

import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Lock, Check, Atom, Beef, Wheat, Droplet, Dumbbell, Sparkles, ChevronRight, Target } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function PhasePreviewModal({ open, phaseId, status = "locked", onClose }) {
  const [phase, setPhase] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !phaseId) return;
    setLoading(true);
    fetch(`${API}/api/keto-fusion/science/${phaseId}`)
      .then((r) => r.json())
      .then(setPhase)
      .catch(() => setPhase(null))
      .finally(() => setLoading(false));
  }, [open, phaseId]);

  if (!open) return null;

  const isLocked = status === "locked";
  const isPassed = status === "passed";
  const color = phase?.color || "#84cc16";

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/90 backdrop-blur-md flex items-center justify-center z-50 p-4 overflow-y-auto"
        onClick={onClose}
        data-testid={`phase-preview-${phaseId}`}
      >
        {/* Ambient glow */}
        <div className="fixed inset-0 pointer-events-none opacity-40">
          <motion.div
            className="absolute top-1/4 left-1/4 w-96 h-96 rounded-full blur-[120px]"
            style={{ background: color }}
            animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0.6, 0.3] }}
            transition={{ duration: 6, repeat: Infinity }}
          />
          <motion.div
            className="absolute bottom-1/4 right-1/4 w-96 h-96 rounded-full blur-[120px]"
            style={{ background: color }}
            animate={{ scale: [1, 1.15, 1], opacity: [0.2, 0.5, 0.2] }}
            transition={{ duration: 7, repeat: Infinity, delay: 1.5 }}
          />
        </div>

        <motion.div
          initial={{ scale: 0.9, y: 40 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.9, y: 40 }}
          transition={{ type: "spring", stiffness: 260, damping: 25 }}
          className="relative bg-zinc-950 border rounded-3xl p-6 sm:p-8 max-w-2xl w-full my-8"
          style={{ borderColor: `${color}50`, boxShadow: `0 40px 100px ${color}25` }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Close */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 w-8 h-8 rounded-full bg-zinc-900/80 hover:bg-zinc-800 flex items-center justify-center z-10"
            data-testid="phase-preview-close"
          >
            <X className="w-4 h-4 text-zinc-400" />
          </button>

          {loading || !phase ? (
            <div className="py-20 text-center">
              <motion.div animate={{ rotate: 360 }} transition={{ duration: 1.4, repeat: Infinity, ease: "linear" }} className="inline-block">
                <Atom className="w-10 h-10" style={{ color }} />
              </motion.div>
            </div>
          ) : (
            <>
              {/* Status Badge */}
              <div className="flex items-center gap-2 mb-3">
                {isLocked && (
                  <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-zinc-900 border border-zinc-800">
                    <Lock className="w-3 h-3 text-zinc-500" />
                    <span className="text-[10px] uppercase tracking-widest text-zinc-500">Bloqueada · Preview</span>
                  </div>
                )}
                {isPassed && (
                  <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full" style={{ background: `${color}20`, border: `1px solid ${color}40` }}>
                    <Check className="w-3 h-3" style={{ color }} />
                    <span className="text-[10px] uppercase tracking-widest" style={{ color }}>Concluída</span>
                  </div>
                )}
              </div>

              {/* Hero */}
              <div className="mb-6">
                <p className="text-[10px] uppercase tracking-widest mb-1" style={{ color }}>
                  Bloco {phase.block} · {phase.duration_days} dias · Semanas {phase.weeks?.join("–")}
                </p>
                <h2 className="text-3xl sm:text-4xl font-black tracking-tight text-zinc-100 mb-2" style={{ textShadow: `0 0 30px ${color}40` }}>
                  {phase.name_pt}
                </h2>
                <p className="text-sm italic text-zinc-400">{phase.tagline_pt}</p>
              </div>

              {/* Objective */}
              <div className="mb-5 p-4 rounded-2xl bg-zinc-900/40 border border-zinc-800">
                <p className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2 flex items-center gap-1">
                  <Target className="w-3 h-3" /> Objectivo Fisiológico
                </p>
                <p className="text-sm text-zinc-200 leading-relaxed">{phase.objective_pt}</p>
              </div>

              {/* Mechanisms preview */}
              <p className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2 flex items-center gap-1">
                <Atom className="w-3 h-3" /> Mecanismos-chave (peer-reviewed)
              </p>
              <div className="grid sm:grid-cols-2 gap-2 mb-5">
                {phase.mechanisms?.slice(0, 4).map((m, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.06 }}
                    className="p-3 rounded-xl bg-zinc-900/30 border border-zinc-800"
                  >
                    <h4 className="text-xs font-semibold mb-1" style={{ color }}>{m.title_pt}</h4>
                    <p className="text-[11px] text-zinc-400 leading-snug line-clamp-3">{m.body_pt}</p>
                  </motion.div>
                ))}
              </div>

              {/* What you'll do */}
              <div className="grid sm:grid-cols-2 gap-3 mb-5">
                <div className="p-4 rounded-xl bg-zinc-900/30 border border-lime-500/20">
                  <p className="text-[10px] uppercase tracking-widest text-lime-400 mb-1.5 flex items-center gap-1">
                    <Dumbbell className="w-3 h-3" /> Treino desta fase
                  </p>
                  <p className="text-[11px] text-zinc-300 leading-snug">{phase.training_principle_pt}</p>
                </div>
                <div className="p-4 rounded-xl bg-zinc-900/30 border border-purple-500/20">
                  <p className="text-[10px] uppercase tracking-widest text-purple-400 mb-1.5 flex items-center gap-1">
                    <Wheat className="w-3 h-3" /> Nutrição desta fase
                  </p>
                  <p className="text-[11px] text-zinc-300 leading-snug">{phase.fueling_principle_pt}</p>
                </div>
              </div>

              {/* KPIs to unlock */}
              {phase.kpis?.length > 0 && (
                <div className="mb-5">
                  <p className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2 flex items-center gap-1">
                    <Sparkles className="w-3 h-3" /> {isLocked ? "O que vais atingir" : "O que atingiste"}
                  </p>
                  <div className="grid grid-cols-2 gap-2">
                    {phase.kpis.map((k, i) => (
                      <div key={i} className="p-2.5 rounded-lg bg-zinc-900/30 border border-zinc-800">
                        <p className="text-[10px] text-zinc-500">{k.label_pt}</p>
                        <p className="text-sm font-bold" style={{ color }}>{k.target_pt}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Footer */}
              {isLocked && (
                <div className="p-3 rounded-xl border border-zinc-800 bg-zinc-900/50 flex items-center gap-3">
                  <Lock className="w-4 h-4 text-zinc-500 flex-shrink-0" />
                  <p className="text-[11px] text-zinc-400 leading-snug">
                    Esta fase desbloqueia quando concluíres os anéis da fase actual.
                    Foca-te em uma coisa de cada vez — a excelência vem da consolidação.
                  </p>
                </div>
              )}
            </>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
